// AiSessionsPanel.jsx — right-side Sessions history for Ai

import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from 'components/button/Button';
import ContextMenu from 'components/contextmenu/ContextMenu';
import { useTheme } from 'context/ThemeContext';
import {
  AI_LEGACY_FEED_TITLES,
  AI_NEW_CHAT_TITLE,
  AI_UPDATES_TITLE,
} from 'spaces/constants/aiConstants';
import {
  createAiSession,
  deleteAiSession,
  updateAiSessionMeta,
} from 'spaces/services/aiConversationService';
import { isAiFeedSession } from 'spaces/utils/aiChannelKeys';
import { getRelativeTimeShort } from 'utilities/DateTime';
import { getPersonName } from 'utilities/PersonIdentity';

import './AiSessionsPanel.css';

function cleanString(value) {
  return String(value || '').trim();
}

function sessionDisplayTitle(session, baseKey, userKey) {
  const title = cleanString(session?.title);
  const isFeed = isAiFeedSession(session, baseKey, userKey);
  if (isFeed) {
    if (!title || AI_LEGACY_FEED_TITLES.includes(title.toLowerCase())) {
      return AI_UPDATES_TITLE;
    }
    return title;
  }
  if (title) return title;
  return AI_NEW_CHAT_TITLE;
}

const SessionRow = React.memo(({
  session,
  active,
  baseKey,
  userKey,
  onSelect,
  onOpenMenu,
}) => {
  const { theme } = useTheme();
  const title = sessionDisplayTitle(session, baseKey, userKey);
  const timeLabel = session?.lastMessageAt
    ? getRelativeTimeShort(session.lastMessageAt)
    : '';

  return (
    <div className={`ai-session-row-wrap${active ? ' is-active' : ''}`}>
      <button
        type="button"
        className="ai-session-row"
        onClick={() => onSelect?.(session)}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenMenu?.(session, {
            x: event.clientX,
            y: event.clientY,
          });
        }}
      >
        <span className="ai-session-row__title">{title}</span>
        <span className="ai-session-row__meta">
          {session?.pinned === true ? 'Pinned · ' : ''}
          {timeLabel}
        </span>
      <Button
        className="ai-session-row__more"
        title="Session actions"
        iconKey="more_vert"
        iconSize="20"
        iconColor={theme.foregroundMuted}
        height="28px"
        width="28px"
        padding="4px"
        borderRadius="6px"
        backgroundColor="transparent"
        onClick={(event) => {
          event?.stopPropagation?.();
          const rect = event?.currentTarget?.getBoundingClientRect?.();
          onOpenMenu?.(session, rect
            ? { x: rect.left, y: rect.bottom + 4 }
            : { x: event?.clientX || 0, y: event?.clientY || 0 });
        }}
      />
      </button>
    </div>
  );
});

SessionRow.displayName = 'SessionRow';

const AiSessionsPanel = React.memo(({
  sessions = [],
  activeSessionKey = '',
  baseKey,
  user,
  asDrawer = false,
  onClose,
  onOpenSession,
  onSessionCreated,
  onSessionDeleted,
  /** When set, New chat uses this instead of creating an empty draft session. */
  onNewChat,
}) => {
  const { theme } = useTheme();
  const userKey = cleanString(user?.key);
  const [menuState, setMenuState] = useState({
    open: false,
    session: null,
    anchorPoint: null,
  });
  const [renamingSession, setRenamingSession] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);
  const renameInputRef = useRef(null);

  const menuSession = menuState.session;
  const menuIsFeed = isAiFeedSession(menuSession, baseKey, userKey);

  useEffect(() => {
    if (renamingSession) {
      renameInputRef.current?.focus?.();
      renameInputRef.current?.select?.();
    }
  }, [renamingSession]);

  const closeMenu = useCallback(() => {
    setMenuState({
      open: false,
      session: null,
      anchorPoint: null,
    });
  }, []);

  const handleOpenMenu = useCallback((session, anchorPoint) => {
    setMenuState({
      open: true,
      session,
      anchorPoint: anchorPoint || { x: 0, y: 0 },
    });
  }, []);

  const handleNewChat = useCallback(async () => {
    if (typeof onNewChat === 'function') {
      onClose?.();
      onNewChat();
      return;
    }

    if (!baseKey || !userKey || busy) return;
    setBusy(true);
    try {
      const result = await createAiSession({
        baseKey,
        userKey,
        userName: getPersonName(user, 'Member'),
      });
      const channelKey = cleanString(result?.channel?.key);
      if (channelKey) {
        onSessionCreated?.(result.channel);
        onOpenSession?.(channelKey);
      }
    } catch (error) {
      console.error('[Sessions] new chat failed', error);
    } finally {
      setBusy(false);
    }
  }, [busy, onClose, onNewChat, onOpenSession, onSessionCreated, user, userKey, baseKey]);

  const handlePin = useCallback(async (session) => {
    closeMenu();
    if (!session?.key) return;
    try {
      await updateAiSessionMeta({
        baseKey,
        channelKey: session.key,
        pinned: session?.pinned !== true,
        userKey,
      });
    } catch (error) {
      console.error('[Sessions] pin failed', error);
    }
  }, [closeMenu, userKey, baseKey]);

  const handleStartRename = useCallback((session) => {
    closeMenu();
    if (!session) return;
    setRenamingSession(session);
    setRenameValue(sessionDisplayTitle(session, baseKey, userKey));
  }, [closeMenu, userKey, baseKey]);

  const handleCommitRename = useCallback(async () => {
    if (!renamingSession?.key) return;
    const next = cleanString(renameValue);
    if (!next) {
      setRenamingSession(null);
      return;
    }
    try {
      await updateAiSessionMeta({
        baseKey,
        channelKey: renamingSession.key,
        title: next,
        userKey,
      });
    } catch (error) {
      console.error('[Sessions] rename failed', error);
    } finally {
      setRenamingSession(null);
    }
  }, [renameValue, renamingSession, userKey, baseKey]);

  const handleDelete = useCallback(async (session) => {
    closeMenu();
    if (!session?.key) return;
    if (isAiFeedSession(session, baseKey, userKey)) return;
    const title = sessionDisplayTitle(session, baseKey, userKey);
    const confirmed = window.confirm(`Delete “${title}”? Messages will be removed.`);
    if (!confirmed) return;
    try {
      await deleteAiSession({
        baseKey,
        channelKey: session.key,
        userKey,
      });
      onSessionDeleted?.(session.key);
    } catch (error) {
      console.error('[Sessions] delete failed', error);
      const code = String(error?.code || '');
      const message = code.includes('permission-denied')
        ? 'You do not have permission to delete this session.'
        : (error?.message || 'Could not delete session.');
      window.alert(message);
    }
  }, [closeMenu, onSessionDeleted, userKey, baseKey]);

  const body = (
    <>
      <div className="ai-sessions-panel__header">
        <h2 className="ai-sessions-panel__title">Sessions</h2>
        <Button
          title={asDrawer ? 'Close' : 'Collapse sessions'}
          iconKey="close"
          iconColor={theme.foregroundMuted}
          backgroundColor="transparent"
          hoverForegroundColor={theme.foreground}
          iconSize="18"
          height="32px"
          width="32px"
          padding="4px"
          borderRadius="8px"
          onClick={() => onClose?.()}
        />
      </div>

      <div className="ai-sessions-panel__new">
        <Button
          className="ai-sessions-panel__new-btn"
          label="New Session"
          iconKey="add"
          iconSize="16"
          height="36px"
          fontSize="12px"
          borderRadius="8px"
          //backgroundColor={theme.backgroundHover}
          //foregroundColor={theme.foreground}
          enabled={!busy}
          onClick={handleNewChat}
        />
      </div>

      <div className="ai-sessions-panel__section-label">Recents</div>

      <div className="ai-sessions-panel__list">
        {sessions.length === 0 ? (
          <div className="ai-sessions-panel__empty">
            New sessions you start will show up here.
          </div>
        ) : (
          sessions.map((session) => (
            <SessionRow
              key={session.key}
              session={session}
              active={session.key === activeSessionKey}
              baseKey={baseKey}
              userKey={userKey}
              onSelect={(row) => {
                closeMenu();
                onOpenSession?.(row.key);
              }}
              onOpenMenu={handleOpenMenu}
            />
          ))
        )}

        {renamingSession ? (
          <div className="ai-sessions-rename">
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleCommitRename();
                }
                if (event.key === 'Escape') {
                  setRenamingSession(null);
                }
              }}
              aria-label="Rename session"
            />
            <Button
              label="Save"
              height="32px"
              fontSize="13"
              borderRadius="8px"
              onClick={() => void handleCommitRename()}
            />
          </div>
        ) : null}
      </div>

      <ContextMenu
        open={menuState.open && Boolean(menuSession)}
        setOpen={(nextOpen) => {
          if (!nextOpen) closeMenu();
        }}
        anchorPoint={menuState.anchorPoint}
      >
        <ContextMenu.Item
          iconKey="keep"
          label={menuSession?.pinned === true ? 'Unpin' : 'Pin'}
          onClick={() => handlePin(menuSession)}
        />
        <ContextMenu.Item
          iconKey="edit"
          label="Rename…"
          onClick={() => handleStartRename(menuSession)}
        />
        {!menuIsFeed ? (
          <ContextMenu.Item
            iconKey="delete"
            label="Delete…"
            onClick={() => handleDelete(menuSession)}
          />
        ) : null}
      </ContextMenu>
    </>
  );

  if (asDrawer) {
    return (
      <>
        <button
          type="button"
          className="ai-sessions-drawer-backdrop"
          aria-label="Close sessions"
          onClick={() => onClose?.()}
        />
        <aside className="ai-sessions-drawer" aria-label="Sessions">
          {body}
        </aside>
      </>
    );
  }

  return (
    <aside className="ai-sessions-panel" aria-label="Sessions">
      {body}
    </aside>
  );
});

AiSessionsPanel.displayName = 'AiSessionsPanel';

export default AiSessionsPanel;
